import { useRef, useState } from "react";
import { PartyPopper } from "lucide-react";
import { BECOME_DSA_POINTS } from "@/data/content";
import dsaCareerService from "@/services/dsaCareer";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import TurnstileWidget from "@/components/TurnstileWidget";

export default function DSACareerSection() {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    experience: "",
    currentJob: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!turnstileToken) {
      toast.error("Please complete the security verification.");
      return;
    }

    setIsSubmitting(true);

    try {
      await dsaCareerService.create({
        fullName: formData.name.trim(),
        mobile: formData.mobile.trim(),
        email: formData.email.trim(),
        currentJob: formData.currentJob.trim(),
        pastExperience: formData.experience.trim() || null,
        message: formData.message.trim() || null,

        turnstileToken,
        turnstileAction: "dsa-enquiry",
      });

      setDone(true);
      setTurnstileToken("");
      toast.success(
        "Thank you for your interest. Our team will get in touch with you soon.",
      );
    } catch (error) {
      console.error("DSA enquiry submission failed:", error);

      turnstileRef.current?.reset();
      setTurnstileToken("");

      const response = error?.response?.data;

      if (response?.errors?.length) {
        const errorMessage = response.errors
          .map((item) => item.message)
          .filter(Boolean)
          .join(", ");

        toast.error("Submission failed", {
          description: errorMessage,
        });
      } else {
        toast.error("Submission failed", {
          description:
            response?.message ||
            "Something went wrong while submitting your details. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="dsa-career"
      className="relative overflow-hidden bg-slate-900 py-16 sm:py-20 lg:py-24"
    >
      {/* Background decoration */}
      <div
        className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl"
        aria-hidden="true"
      />

      <div
        className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          {/* Content */}
          <div className="text-white">
            <div className="mb-5 inline-flex items-center rounded-full border border-blue-400/30 bg-blue-400/10 px-4 py-2 text-sm font-medium text-blue-300">
              Become a DSA
            </div>

            <h2 className="max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Build a Career in the
              <span className="text-blue-400">
                {" "}
                Financial Services Industry
              </span>
            </h2>

            <p className="mt-5 max-w-xl text-base leading-7 text-slate-300 sm:text-lg">
              Looking to build a career as a Direct Selling Agent (DSA)? Money
              Mart can help you get started. Work with a trusted loan
              facilitation team, connect customers with the right lending
              solutions, and build a long-term career in financial services.
            </p>

            <div className="mt-8 space-y-5">
              {BECOME_DSA_POINTS.map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-blue-400">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path d="M5 12l4 4L19 6" />
                    </svg>
                  </div>

                  <div>
                    <h3 className="font-semibold text-white">{item.title}</h3>

                    <p className="mt-1 text-sm leading-6 text-slate-400">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
            {done ? (
              <DSASuccessView />
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900">
                    Interested in becoming a DSA?
                  </h3>

                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    Share your details and our team will contact you to discuss
                    the opportunity.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="dsa-name"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      Full Name *
                    </label>

                    <Input
                      id="dsa-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  {/* Mobile + Email */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="dsa-mobile"
                        className="mb-1.5 block text-sm font-medium text-slate-700"
                      >
                        Mobile Number *
                      </label>

                      <Input
                        id="dsa-mobile"
                        name="mobile"
                        type="tel"
                        value={formData.mobile}
                        onChange={handleChange}
                        required
                        placeholder="Enter mobile number"
                        className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="dsa-email"
                        className="mb-1.5 block text-sm font-medium text-slate-700"
                      >
                        Email Address *
                      </label>

                      <Input
                        id="dsa-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter email address"
                        className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      />
                    </div>
                  </div>

                  {/* Current Job */}
                  <div>
                    <label
                      htmlFor="dsa-current-job"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      Current Job / Profession *
                    </label>

                    <Input
                      id="dsa-current-job"
                      name="currentJob"
                      type="text"
                      value={formData.currentJob}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Real Estate Agent, Insurance Agent, Business Owner"
                      className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  {/* Experience */}
                  <div>
                    <label
                      htmlFor="dsa-experience"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      Past Experience
                      <span className="ml-1 font-normal text-slate-400">
                        (Optional)
                      </span>
                    </label>

                    <Textarea
                      id="dsa-experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Tell us briefly about your previous experience, if any"
                      className="w-full resize-none rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="dsa-message"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      Message
                      <span className="ml-1 font-normal text-slate-400">
                        (Optional)
                      </span>
                    </label>

                    <Textarea
                      id="dsa-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Tell us anything else you would like us to know"
                      className="w-full resize-none rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                    />
                  </div>

                  <TurnstileWidget
                    ref={turnstileRef}
                    action="dsa-enquiry"
                    onTokenChange={setTurnstileToken}
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting || !turnstileToken}
                    className="w-full rounded-lg bg-blue-600 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : "I'm Interested in Becoming a DSA"}
                  </button>

                  <p className="text-center text-xs leading-5 text-slate-400">
                    Our team will contact you to discuss the DSA opportunity.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function DSASuccessView() {
  return (
    <div
      data-testid="dsa-form-success"
      className="flex min-h-[420px] flex-col items-center justify-center text-center"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-500/10">
        <PartyPopper className="h-7 w-7 text-blue-600" />
      </div>

      <h3 className="mt-5 text-2xl font-bold tracking-tight text-slate-900">
        Thank you for your interest
      </h3>

      <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
        We have received your details. Our team will get in touch with you
        shortly to discuss the DSA opportunity.
      </p>
    </div>
  );
}
